import { Component, inject, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { SaleOrder, Partner } from '@app/shared';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { base64ToFile, CommunicationService, DrawerEscapeBlockerService, OpportunityService, GlobalValuesService } from '@app/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { ToastConfig } from '@app/shared';
import { ViewChild } from '@angular/core';

export interface MinPartnerDto {
  id: number;
  avatar: string | null;
  name: string;
  surName: string | null;
  email: string | null;
  position: string | null;
  odooEntityId: number | null;
}

export interface EmailTemplateDto {
  id: number;
  subject: string;
  body: string; // HTML string
  recipients: MinPartnerDto[];
  attachments?: string[]; // base64 strings (opcional, puede no venir)
}
@Component({
  selector: 'app-send-budget-drawer',
  standalone: false,

  templateUrl: './send-budget-drawer.component.html',
  styleUrl: './send-budget-drawer.component.less',
})
export class SendBudgetDrawerComponent implements OnInit, OnDestroy {
  @ViewChild('notificationTmpl') notificationTmpl: any;
  notRef: any;
  toastConfig: ToastConfig = {
    icon: 'check_circle',
    iconColor: '#6CDA75',
    content: 'Presupuesto enviado correctamente.',
    showUndo: false,
  };

  nzData: {
    budgetId?: string | number;
    budget?: SaleOrder;
  } = inject(NZ_DRAWER_DATA);

  emailForm!: FormGroup;
  loading = false;
  emailTemplate: EmailTemplateDto | null = null;
  // availableRecipients: MinPartnerDto[] = []; // Ya no usamos esto restringido
  availableRecipients: Partner[] = []; // Usamos Partner completo
  fileList: NzUploadFile[] = [];

  quillConfig = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'], // toggled buttons
      ['blockquote', 'code-block'],
      [{ header: 1 }, { header: 2 }], // custom button values
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
      [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
      [{ direction: 'rtl' }], // text direction
      [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }], // dropdown with defaults from theme
      [{ font: [] }],
      [{ align: [] }],
      ['clean'], // remove formatting button
      ['link'], // link
    ],
  };

  private destroy$ = new Subject<void>();

  constructor(
    private drawerRef: NzDrawerRef<string>,
    private fb: FormBuilder,
    private opportunityService: OpportunityService,
    private communicationService: CommunicationService,
    private notification: NzNotificationService,
    private cdr: ChangeDetectorRef,
    private globalValuesService: GlobalValuesService,
    private drawerEscapeBlocker: DrawerEscapeBlockerService,
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    if (!this.nzData.budget) {
      this.toastConfig.content = 'Mensaje enviado correctamente.';
    }
    this.loadEmailTemplate();
    this.subscribeToPartners();

    // Registrar handler nativo en fase de captura para interceptar Escape
    this.drawerEscapeBlocker.block();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Remover listener nativo si fue registrado
    this.drawerEscapeBlocker.block();
  }

  initForm(): void {
    this.emailForm = this.fb.group({
      to: [[], Validators.required],
      cc: [[]],
      subject: ['', Validators.required],
      body: ['', Validators.required],
    });
  }

  subscribeToPartners(): void {
    this.globalValuesService.isPartnerReady$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isReady: boolean) => {
        if (isReady) {
          this.availableRecipients = this.globalValuesService.partnerlist || [];
          this.cdr.detectChanges();
        }
      });
  }

  loadEmailTemplate(): void {
    const budgetId = this.nzData.budgetId || this.nzData.budget?.id;
    if (!budgetId) {
      this.loading = false;
      return;
    }

    this.loading = true;
    this.cdr.detectChanges();

    this.opportunityService
      .getEmailTemplate(budgetId)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.emailTemplate = response.data as EmailTemplateDto;
            const defaultRecipients: number[] = [];
            if (
              this.emailTemplate.recipients &&
              this.emailTemplate.recipients.length > 0
            ) {
              defaultRecipients.push(
                ...this.emailTemplate.recipients.map((r) => r.id),
              );
            }

            // Todos los destinatarios van en "Para"
            this.emailForm.patchValue({
              to: defaultRecipients,
              cc: [],
              subject: this.emailTemplate.subject || '',
              body: this.emailTemplate.body || '',
            });

            // Cargar adjuntos si vienen en la respuesta (base64)
            if (
              this.emailTemplate.attachments &&
              this.emailTemplate.attachments.length > 0
            ) {
              const budgetName = this.nzData.budget?.name || 'Presupuesto';
              const attachmentsCount = this.emailTemplate.attachments.length;
              const newFiles: NzUploadFile[] =
                this.emailTemplate.attachments.map((base64, index) => {
                  // Usar el nombre del presupuesto para el PDF
                  const filename =
                    attachmentsCount === 1
                      ? `${budgetName}.pdf`
                      : `${budgetName}_${index + 1}.pdf`;
                  const file = base64ToFile(base64, filename);
                  return {
                    uid: `init-${index}`,
                    name: filename,
                    status: 'done',
                    originFileObj: file,
                  } as NzUploadFile;
                });
              this.fileList = [...this.fileList, ...newFiles];
            }

            this.cdr.detectChanges();
          } else {
            console.warn(
              'Response does not have expected structure:',
              response,
            );
            this.cdr.detectChanges();
          }
        },
        error: (error) => {
          console.error('Error loading email template:', error);
          this.cdr.detectChanges();
        },
      });
  }

  // Eliminados onRecipientsChange y onCcChange - ahora usamos valueChanges del form

  beforeUpload = (file: NzUploadFile): boolean => {
    // Permitir múltiples archivos (concatenar)
    this.fileList = this.fileList.concat(file);
    this.cdr.detectChanges();
    return false; // Retornar false para carga manual
  };

  removeFile(file: NzUploadFile): void {
    this.fileList = this.fileList.filter((f) => f.uid !== file.uid);
    this.cdr.detectChanges();
  }

  sendEmail(): void {
    if (this.emailForm.invalid) {
      Object.values(this.emailForm.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      this.notification.warning(
        'Advertencia',
        'Por favor complete todos los campos requeridos',
      );
      return;
    }

    this.loading = true;
    const formValue = this.emailForm.value;

    const toIds: number[] = formValue.to || [];
    const ccIds: number[] = formValue.cc || [];

    // Combinar "Para" y "CC" para obtener todos los destinatarios
    const allRecipientIds = [...toIds, ...ccIds];
    const selectedRecipients = this.availableRecipients.filter((r) =>
      allRecipientIds.includes(r.id),
    );

    const emails = selectedRecipients
      .map((r) => r.email)
      .filter((e): e is string => !!e && e.trim().length > 0);

    if (!emails.length) {
      this.loading = false;
      this.notification.error(
        'Error',
        'Los destinatarios seleccionados no tienen correo configurado',
      );
      return;
    }

    const toEmailAddress = emails.join(';');
    const toName = selectedRecipients
      .map((r) => this.getRecipientLabel(r))
      .join(', ');

    const htmlBody = formValue.body as string;
    const attachments: File[] = [];
    this.fileList.forEach((file) => {
      if (file.originFileObj) {
        attachments.push(file.originFileObj as File);
      } else if (file instanceof File) {
        attachments.push(file);
      }
    });

    this.communicationService
      .sendEmail({
        toEmailAddress,
        subject: formValue.subject,
        plainTextContent: htmlBody.replace(/<[^>]*>/g, ''), // Una conversión simple a texto plano por si el back lo necesita
        htmlContent: htmlBody,
        toName,
        attachments: attachments.length > 0 ? attachments : undefined,
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.notRef = this.notification.create(
              'blank',
              '',
              this.notificationTmpl,
              { nzPlacement: 'bottomRight' },
            );
            this.cdr.detectChanges();
            setTimeout(() => {
              this.drawerRef.close('sent');
            }, 500);
          } else {
            this.notification.error(
              'Error',
              response.message || 'No se pudo enviar el correo',
            );
          }
        },
        error: (error) => {
          this.notification.error('Error', 'No se pudo enviar el correo');
          console.error('Error sending email:', error);
        },
      });
  }

  getRecipientLabel(recipient: Partner | MinPartnerDto): string {
    if (recipient.surName) {
      return `${recipient.name} ${recipient.surName}`;
    }
    return recipient.name;
  }

  saveAsTemplate(): void {
    // Implementar lógica para guardar como plantilla si es necesario
    this.notification.info(
      'Info',
      'Funcionalidad de guardar plantilla pendiente de implementar',
    );
  }

  close(): void {
    this.drawerRef.close();
  }
}
